import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Space, message, Table, Modal, Checkbox } from "antd";

import { DeleteListingAssetsThunk, GetListingAssetsThunk, UpdateListingAssetsThunk } from "../../Store/Thunks/ListingAssetsThunk";
import { ReactComponent as DeleteIcon } from "../../Assets/img/icons/delete.svg";
import { ReactComponent as EditIcon } from "../../Assets/img/icons/edit.svg";

function requestErrorMessages(error) {
  const messages = [];

  const visit = (value) => {
    if (!value) return;
    if (typeof value === "string") {
      messages.push(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value !== "object") return;
    if (typeof value.message === "string" || Array.isArray(value.message)) {
      visit(value.message);
      return;
    }
    if (value.error) {
      visit(value.error);
    }
  };

  visit(error);
  return messages.length ? messages : ["Something went wrong"];
}

function deleteConfirmContent(assetName, error) {
  return (
    <div>
      <div>
        {`The asset ${assetName} will be permanently deleted and cannot be recovered. Please confirm your action.`}
      </div>
      {error
        ? requestErrorMessages(error).map((text, index) => (
            <div key={`${text}-${index}`} style={{ color: "red", marginTop: 8 }}>
              {text}
            </div>
          ))
        : null}
    </div>
  );
}

export const AssetsLists = ({ setOpen, setAssetId }) => {
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();
  const [checkedState, setCheckedState] = useState({});

  const assetsList = useSelector(
    (state) => state.ListingAssetsReducer.assetsList
  );
  const created = useSelector((state) => state.ListingAssetsReducer.created);
  const updated = useSelector((state) => state.ListingAssetsReducer.updated);
  const deleted = useSelector((state) => state.ListingAssetsReducer.deleted);

  useEffect(() => {
    dispatch(GetListingAssetsThunk());
  }, []);

  useEffect(() => {
    if (created || updated || deleted) {
      setOpen(false);
      dispatch(GetListingAssetsThunk());
    }
  }, [created, updated, deleted]);

  useEffect(() => {
    if (created) {
      setOpen(false);
    }
  }, [created]);

  const handleCheckboxChange = (id, checked) => {
    setCheckedState((prev) => ({ ...prev, [id]: checked }));
    const show = checked ? true : false;
    dispatch(UpdateListingAssetsThunk({ show: show }, id));
  };

  const handleDeleteAsset = (id, assetName) => {
    const deletingRef = { current: false };
    const modal = Modal.confirm({
      okText: "Delete",
      cancelText: "Abort",
      maskClosable: false,
      keyboard: false,
      content: deleteConfirmContent(assetName),
      onOk: () => {
        deletingRef.current = true;
        modal.update({
          keyboard: false,
          okButtonProps: { loading: true },
          cancelButtonProps: { disabled: true },
        });

        return dispatch(DeleteListingAssetsThunk(id, 1))
          .then(() => {
            messageApi.open({
              type: "success",
              content: `The ${assetName} asset was successfully deleted`,
            });
          })
          .catch((error) => {
            deletingRef.current = false;
            modal.update({
              keyboard: true,
              okButtonProps: { loading: false },
              cancelButtonProps: { disabled: false },
              content: deleteConfirmContent(assetName, error),
            });
            return Promise.reject(error);
          });
      },
      onCancel: (close) => {
        if (deletingRef.current) return;
        close();
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Asset",
      dataIndex: "assetName",
      key: "assetName",
      width: 250,
      ellipsis: true,
    },
    {
      title: "Asset Slug",
      dataIndex: "assetSlug",
      key: "assetSlug",
      width: 250,
      ellipsis: true,
    },
    {
      title: "CoinBase Name",
      dataIndex: "coinbaseName",
      key: "coinbaseName",
      width: 250,
      ellipsis: true,
    },
    {
      title: "Maturity",
      dataIndex: "maturity",
      key: "maturity",
    },
    {
      title: "Security",
      dataIndex: "security",
      key: "security",
    },
    {
      title: "Founders",
      dataIndex: "founders",
      key: "founders",
    },
    {
      title: "Reputation",
      dataIndex: "reputation",
      key: "reputation",
    },
    {
      title: "AML",
      dataIndex: "aml",
      key: "aml",
    },
    {
      title: (
        <Space>
          <span>Actions</span>
        </Space>
      ),
      width: 150,
      render: (record, { id, assetName }) => {
        return (
          <Space size="small">
            <EditIcon
              onClick={() => {
                setOpen(true);
                setAssetId(id);
              }}
            />
            <DeleteIcon onClick={() => handleDeleteAsset(id, assetName)} />
            <Checkbox
              className="custom-checkbox"
              checked={record.show ? true : false}
              onChange={(e) => handleCheckboxChange(id, e.target.checked)}
            />
          </Space>
        );
      },
    },
  ];


  return (
    <>
      {contextHolder}

      <Table
        columns={columns}
        dataSource={assetsList}
        pagination={false}
        scroll={{ y: 500 }}
      />
    </>
  );
};
